import { widgetScriptLocation } from 'constants/dom';
import { checkJwtInterval, pingInterval } from 'constants/general';
import { WorkerMessageType } from 'enums/WorkerMessage';
import { WorkerMessage } from 'interfaces/WorkerMessage';

import { createEventTarget } from './event';

interface WidgetWorkerData {
    worker: EventTarget;
    subscribe: <TMessageType extends WorkerMessageType>(
        type: TMessageType,
        handler: (msg: WorkerMessage<TMessageType>) => void,
    ) => void;
    destroy: () => void;
}

type InitWidgetWorker = () => WidgetWorkerData;

const createMockWidgetWorker: InitWidgetWorker = () => {
    const worker = createEventTarget();
    const mockWorkerTimers = [] as ReturnType<typeof setInterval>[];
    const subscriptionCancellations = [] as (() => void)[];

    mockWorkerTimers.push(
        setInterval(() => {
            worker.dispatchEvent(
                new CustomEvent('message', {
                    detail: { type: WorkerMessageType.Ping },
                }),
            );
        }, pingInterval * 1000),
    );

    mockWorkerTimers.push(
        setInterval(() => {
            worker.dispatchEvent(
                new CustomEvent('message', {
                    detail: { type: WorkerMessageType.CheckJwt },
                }),
            );
        }, checkJwtInterval * 1000),
    );

    return {
        worker,
        subscribe: (type, handler) => {
            const listener = (e: CustomEvent) => {
                if (e.detail.type === type) {
                    handler(e.detail);
                }
            };

            worker.addEventListener('message', listener);

            const cancelSubscription = () => worker.removeEventListener('message', listener);

            subscriptionCancellations.push(cancelSubscription);

            return cancelSubscription;
        },
        destroy: () => {
            for (const timerId of mockWorkerTimers) {
                try {
                    clearInterval(timerId);
                } catch {}
            }

            for (const cancelSubscription of subscriptionCancellations) {
                try {
                    cancelSubscription();
                } catch {}
            }
        },
    };
};

const createWebWidgetWorker: InitWidgetWorker = () => {
    const worker = new Worker(`${widgetScriptLocation}/widgetWorker.js?v=${BUILD_VERSION}`);
    const subscriptionCancellations = [] as (() => void)[];

    return {
        worker,
        subscribe: (type, handler) => {
            const listener = (e: MessageEvent) => {
                if (e.data.type === type) {
                    handler(e.data);
                }
            };

            worker.addEventListener('message', listener);

            const cancelSubscription = () => worker.removeEventListener('message', listener);

            subscriptionCancellations.push(cancelSubscription);

            return cancelSubscription;
        },
        destroy: () => {
            for (const cancelSubscription of subscriptionCancellations) {
                try {
                    cancelSubscription();
                } catch {}
            }

            worker.terminate();
        },
    };
};

let mockWorker: WidgetWorkerData;
let webWorker: WidgetWorkerData;
let activeWorker: WidgetWorkerData;

let useWebWorkers: boolean = undefined;
export const setUseWebWorkers = (use: boolean) => {
    // TODO: Remove after fixing the issue with cross-domain workers
    const storedUse = localStorage.getItem('_widgetEnableWorkers');
    if (!(storedUse && JSON.parse(storedUse))) {
        use = false;
    }

    if (useWebWorkers === use) {
        return;
    }

    if (activeWorker) {
        activeWorker.destroy();
    }

    if (use) {
        webWorker ??= createWebWidgetWorker();
        activeWorker = webWorker;
    } else {
        mockWorker ??= createMockWidgetWorker();
        activeWorker = mockWorker;
    }

    useWebWorkers = use;
};

export const onWorkerMessage = <TMessageType extends WorkerMessageType>(
    type: TMessageType,
    handler: (msg: WorkerMessage<TMessageType>) => void,
) => {
    activeWorker.subscribe(type, handler);
};
